import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Pulse, CheckCircle, Clock, XCircle, Users, Bell, MapPin, ArrowsClockwise,
  NavigationArrow, MagnifyingGlass, X, Trash, DownloadSimple,
} from "@phosphor-icons/react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, Polyline } from "react-leaflet";
import L from "leaflet";
import { API_BASE } from "../../lib/api";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const SEV_STYLE = {
  high: "bg-[#F4C7B9] text-[#7A2E18]",
  medium: "bg-[#F9E4B7] text-[#7A5A11]",
  low: "bg-[#E5E2D9] text-[#5C5A55]",
};

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// Motion threshold — a ping within 30 s AND >15 m displacement is
// treated as "moving" and gets a pulsing marker.
const MOTION_WINDOW_MS = 30_000;
const MOTION_MIN_M = 15;

function distanceMeters(a, b) {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// ---- Google Maps JS loader (used only when provider === 'google') ----
let _mapsPromise = null;
function loadGoogleMaps() {
  if (_mapsPromise) return _mapsPromise;
  _mapsPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject("no window");
    if (window.google?.maps) return resolve(window.google.maps);
    if (!GMAPS_KEY) return reject("REACT_APP_GOOGLE_MAPS_API_KEY missing");
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}`;
    s.async = true; s.defer = true;
    s.onload = () => resolve(window.google.maps);
    s.onerror = () => reject("Google Maps failed to load");
    document.head.appendChild(s);
  });
  return _mapsPromise;
}

export default function LiveDashboard() {
  const [kpi, setKpi] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tick, setTick] = useState(0);
  // Iter 172 — layer toggles & map provider
  const [showStaff, setShowStaff] = useState(true);
  const [showCustomers, setShowCustomers] = useState(false);
  const [provider, setProvider] = useState("leaflet"); // "leaflet" | "google"
  // Iter 240 · staff search + location filter
  const [staffQuery, setStaffQuery] = useState("");
  const [staffLocFilter, setStaffLocFilter] = useState("all"); // "all" or role
  const [clearing, setClearing] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Iter 241 · GPS history mode
  const todayStr = new Date().toISOString().slice(0, 10);
  const [historyDate, setHistoryDate] = useState("");
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyStaffId, setHistoryStaffId] = useState("");
  // Track previous positions to detect motion since last poll.
  const prevPositionsRef = useRef(new Map()); // staff_id -> {lat,lng,ts}

  const load = async () => {
    try {
      const [k, a, l] = await Promise.all([
        api.get("/live/kpis"),
        api.get("/alerts?status=open"),
        api.get("/live/staff-locations"),
      ]);
      setKpi(k.data); setAlerts(a.data);
      // Enrich each staff row with motion state relative to the last poll.
      const now = Date.now();
      const enriched = (l.data || []).map(row => {
        const prev = prevPositionsRef.current.get(row.staff_id);
        const cur = { lat: row.latitude, lng: row.longitude,
                       ts: row.updated_at ? Date.parse(row.updated_at) : now };
        let moving = false;
        if (prev && prev.ts !== cur.ts) {
          const d = distanceMeters(prev, cur);
          const ageMs = now - cur.ts;
          moving = d >= MOTION_MIN_M && ageMs <= MOTION_WINDOW_MS;
        }
        const stale = cur.ts && (now - cur.ts) > 5 * 60_000; // >5 min old ping
        return { ...row, moving, stale };
      });
      // Store this snapshot as the new baseline.
      const next = new Map();
      (l.data || []).forEach(r => next.set(r.staff_id, {
        lat: r.latitude, lng: r.longitude,
        ts: r.updated_at ? Date.parse(r.updated_at) : Date.now(),
      }));
      prevPositionsRef.current = next;
      setLocations(enriched);
    } catch (e) { console.error(e); }
  };
  const loadCustomers = async () => {
    try {
      const r = await api.get("/live/customer-locations");
      setCustomers(r.data || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => {
    load();
    const id = setInterval(() => { setTick(t => t + 1); load(); }, 15000);
    return () => clearInterval(id);
  }, []);
  // Iter 172 fix — keep customer layer fresh with each poll AND when the
  // layer is first turned on. Also re-runs when the user hits Refresh
  // (tick change).
  useEffect(() => {
    if (!showCustomers) return;
    loadCustomers();
  }, [showCustomers, tick]);

  const ack = async (id, status) => {
    try { await api.post(`/alerts/${id}/ack`, { status });
      toast.success("Alert " + status); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 240 · bulk clear + CSV export for alerts
  const clearAll = async () => {
    const n = alerts.length;
    if (!n) return;
    if (!window.confirm(
      `Close ALL ${n} open alert${n > 1 ? "s" : ""}? This cannot be undone.`
    )) return;
    setClearing(true);
    try {
      const r = await api.post("/alerts/clear-all",
        { scope: "open", confirm: true });
      toast.success(`Cleared ${r.data.cleared} alert(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setClearing(false);
  };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("rescigo_token");
      const r = await fetch(`${API_BASE}/alerts/export.csv?days=90`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alerts-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Alerts CSV downloaded");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    }
    setExporting(false);
  };

  // Iter 241 · fetch GPS history on date change.
  useEffect(() => {
    if (!historyDate) { setHistoryData(null); return; }
    setHistoryLoading(true);
    const qs = new URLSearchParams({ date: historyDate });
    if (historyStaffId) qs.set("staff_id", historyStaffId);
    api.get(`/live/staff-history?${qs.toString()}`)
      .then(r => setHistoryData(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setHistoryLoading(false));
  }, [historyDate, historyStaffId]);

  // Flatten the selected history for the Leaflet polyline + markers.
  const historyTrail = useMemo(() => {
    if (!historyData?.bundles?.length) return null;
    const target = historyStaffId
      ? historyData.bundles.find(b => b.staff_id === historyStaffId)
      : historyData.bundles[0];
    return target || null;
  }, [historyData, historyStaffId]);

  const inHistoryMode = !!historyDate;

  // Iter 240 · derive filtered staff locations for the map + markers.
  const availableRoles = useMemo(() => {
    const roles = new Set(locations.map(l => l.role).filter(Boolean));
    return Array.from(roles).sort();
  }, [locations]);

  const filteredLocations = useMemo(() => {
    const q = staffQuery.trim().toLowerCase();
    return locations.filter(l => {
      if (staffLocFilter !== "all" && l.role !== staffLocFilter) return false;
      if (!q) return true;
      return (
        (l.staff_name || "").toLowerCase().includes(q) ||
        (l.phone || "").toLowerCase().includes(q) ||
        (l.role || "").toLowerCase().includes(q) ||
        (l.staff_id || "").toLowerCase().includes(q)
      );
    });
  }, [locations, staffQuery, staffLocFilter]);

  const mapCenter = filteredLocations.length > 0
    ? [filteredLocations[0].latitude, filteredLocations[0].longitude]
    : locations.length > 0
      ? [locations[0].latitude, locations[0].longitude]
      : [9.9312, 76.2673]; // Kochi

  const staffCount = filteredLocations.length;
  const movingCount = filteredLocations.filter(l => l.moving).length;

  return (
    <AppShell title="Live Operations" subtitle="Module 6"
      actions={
        <div className="flex items-center gap-2 text-xs text-[#5C5A55]">
          <span className="w-2 h-2 rounded-full bg-[#2D5A27] animate-pulse"/>
          Live · auto-refresh 15s · tick #{tick}
          <button onClick={() => { setTick(t => t + 1); load(); }} data-testid="live-refresh"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md inline-flex items-center gap-1.5 ml-2">
            <ArrowsClockwise size={12}/>Refresh
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="live-kpis">
        <Kpi label="Tasks today" value={kpi?.total_tasks} icon={Pulse}/>
        <Kpi label="Completed" value={kpi?.completed} accent icon={CheckCircle}/>
        <Kpi label="Completion %" value={kpi ? `${kpi.completion_pct}%` : "—"} icon={Pulse}/>
        <Kpi label="Missed" value={kpi?.missed} danger icon={XCircle}/>
        <Kpi label="On duty staff" value={kpi?.on_duty} icon={Users}/>
        <Kpi label="Active staff" value={kpi?.active_staff} icon={Users}/>
        <Kpi label="Open alerts" value={kpi?.alerts_open} danger icon={Bell}/>
        <Kpi label="In progress" value={kpi?.in_progress} icon={Clock}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
          {/* Iter 172 — layer & provider toolbar */}
          <div className="px-6 py-3 border-b border-[#E5E2D9] flex flex-wrap items-center gap-2"
               data-testid="live-map-toolbar">
            <MapPin size={16} color={inHistoryMode ? "#4338CA" : "#2D5A27"} weight="fill"/>
            <div className="font-heading">
              {inHistoryMode ? "GPS history" : "Staff live map"}
            </div>
            <span className="ml-2 text-[11px] text-[#5C5A55]">
              {inHistoryMode
                ? historyLoading
                  ? "Loading…"
                  : historyData
                    ? `${historyData.staff_count} staff · ${historyData.total_pings} pings on ${historyData.date}`
                    : ""
                : `${staffCount} staff · ${movingCount} moving`}
            </span>
            <div className="flex-1"/>
            {/* Iter 241 · Date picker */}
            <div className="inline-flex items-center gap-1 text-[11px]">
              <span className="text-[#5C5A55]">History:</span>
              <input type="date" value={historyDate} max={todayStr}
                     onChange={e => { setHistoryDate(e.target.value); setHistoryStaffId(""); }}
                     data-testid="history-date-picker"
                     className="h-8 px-2 rounded border border-[#E5E2D9] bg-white text-[11px]"/>
              {historyDate && (
                <button onClick={() => { setHistoryDate(""); setHistoryStaffId(""); }}
                        data-testid="history-exit"
                        className="h-8 px-2 rounded border border-[#C25D38] text-[11px] text-[#C25D38] hover:bg-[#C25D38] hover:text-white">
                  Live
                </button>
              )}
            </div>
            <button onClick={() => setShowStaff(v => !v)}
                    data-testid="live-toggle-staff"
                    className={`h-8 px-3 rounded-full text-[11px] font-medium border ${
                      showStaff ? "bg-[#1F3A57] text-white border-[#1F3A57]" : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                    }`}>Staff</button>
            <button onClick={() => setShowCustomers(v => !v)}
                    data-testid="live-toggle-customers"
                    className={`h-8 px-3 rounded-full text-[11px] font-medium border ${
                      showCustomers ? "bg-[#3A7033] text-white border-[#3A7033]" : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                    }`}>Customers {showCustomers && `(${customers.length})`}</button>
            <div className="inline-flex rounded-full border border-[#E5E2D9] overflow-hidden ml-1">
              <button onClick={() => setProvider("leaflet")}
                      data-testid="live-provider-leaflet"
                      className={`h-8 px-3 text-[11px] font-medium ${
                        provider === "leaflet" ? "bg-[#2D5A27] text-white" : "bg-white text-[#5C5A55]"
                      }`}>Leaflet · Free</button>
              <button onClick={() => setProvider("google")}
                      data-testid="live-provider-google"
                      disabled={!GMAPS_KEY}
                      title={!GMAPS_KEY ? "REACT_APP_GOOGLE_MAPS_API_KEY missing" : ""}
                      className={`h-8 px-3 text-[11px] font-medium disabled:opacity-40 ${
                        provider === "google" ? "bg-[#2D5A27] text-white" : "bg-white text-[#5C5A55]"
                      }`}>Google</button>
            </div>
          </div>
          <div className="h-[440px]" data-testid="live-map">
            {inHistoryMode
              ? <HistoryMap trail={historyTrail} loading={historyLoading}/>
              : provider === "leaflet"
                ? <LeafletMap center={mapCenter} showStaff={showStaff}
                               showCustomers={showCustomers}
                               locations={filteredLocations} customers={customers}/>
                : <GoogleMap center={mapCenter} showStaff={showStaff}
                               showCustomers={showCustomers}
                               locations={filteredLocations} customers={customers}/>}
          </div>
          {inHistoryMode ? (
            <HistoryPanel data={historyData} loading={historyLoading}
                             selectedId={historyStaffId}
                             onSelect={setHistoryStaffId}/>
          ) : (
          <>
          {/* Iter 240 · Staff search + location/role filter */}
          <div className="px-6 py-3 border-t border-[#E5E2D9] bg-[#FAFAF7]
                            flex flex-wrap items-center gap-2"
                 data-testid="live-staff-search">
            <div className="relative flex-1 min-w-[220px]">
              <MagnifyingGlass size={14}
                 className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
              <input type="text" value={staffQuery}
                       onChange={e => setStaffQuery(e.target.value)}
                       placeholder="Search field staff by name, phone, role, id…"
                       data-testid="staff-search-input"
                       className="w-full h-9 pl-9 pr-8 border border-[#E5E2D9]
                                   rounded-md text-sm bg-white"/>
              {staffQuery && (
                <button onClick={() => setStaffQuery("")}
                           data-testid="staff-search-clear"
                           className="absolute right-2 top-1/2 -translate-y-1/2
                                       p-1 text-[#8A8780] hover:text-[#C25D38]">
                  <X size={12}/>
                </button>
              )}
            </div>
            <select value={staffLocFilter}
                      onChange={e => setStaffLocFilter(e.target.value)}
                      data-testid="staff-location-filter"
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md
                                 text-xs bg-white">
              <option value="all">All locations · {locations.length}</option>
              {availableRoles.map(r => (
                <option key={r} value={r}>Role: {r}</option>
              ))}
            </select>
            <span className="text-[11px] text-[#5C5A55] ml-1">
              {staffQuery || staffLocFilter !== "all"
                ? `Showing ${filteredLocations.length} of ${locations.length}`
                : `${locations.length} on-duty staff`}
            </span>
          </div>
          {locations.length === 0 && (
            <div className="px-6 py-3 text-xs text-[#8A8780] bg-[#F7F6F2] border-t border-[#E5E2D9]">
              No on-duty staff with live pings. Field staff must clock in from the mobile app;
              logged-out staff don&apos;t appear on the live map.
            </div>
          )}
          </>
          )}
        </div>

        <div className="bg-white border border-[#E5E2D9] rounded-md">
          <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2">
            <Bell size={16} color="#C25D38" weight="fill"/>
            <div className="font-heading">Alerts</div>
            <div className="flex-1"/>
            <button onClick={exportCsv} disabled={exporting}
                      data-testid="alerts-export-csv"
                      title="Download all alerts (last 90 days) as CSV"
                      className="h-8 px-2.5 inline-flex items-center gap-1
                                 text-[11px] border border-[#E5E2D9] rounded
                                 text-[#5C5A55] hover:bg-[#F7F6F2] disabled:opacity-50">
              <DownloadSimple size={12} weight="bold"/>
              {exporting ? "Exporting…" : "CSV"}
            </button>
            <button onClick={clearAll}
                      disabled={clearing || alerts.length === 0}
                      data-testid="alerts-clear-all"
                      title="Mark every open alert as closed"
                      className="h-8 px-2.5 inline-flex items-center gap-1
                                 text-[11px] border border-[#C25D38] rounded
                                 text-[#C25D38] hover:bg-[#C25D38] hover:text-white
                                 disabled:opacity-40 disabled:hover:bg-white
                                 disabled:hover:text-[#C25D38]">
              <Trash size={12} weight="bold"/>
              {clearing ? "Clearing…" : `Clear all${alerts.length ? ` (${alerts.length})` : ""}`}
            </button>
          </div>
          <div className="divide-y divide-[#E5E2D9] max-h-[440px] overflow-y-auto" data-testid="alerts-list">
            {alerts.length === 0 && <div className="px-6 py-10 text-center text-sm text-[#8A8780]">All clear.</div>}
            {alerts.map(a => (
              <div key={a.id} className="px-6 py-3" data-testid={`alert-${a.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${SEV_STYLE[a.severity] || SEV_STYLE.low}`}>{a.severity}</span>
                  <span className="text-[10px] text-[#8A8780] font-mono">{new Date(a.created_at).toLocaleTimeString()}</span>
                </div>
                <div className="text-sm mt-1.5 text-[#1A1A1A]">{a.message}</div>
                <div className="text-[11px] text-[#8A8780] mt-0.5 font-mono">{a.type}</div>
                <div className="flex gap-3 mt-2 text-xs">
                  <button onClick={() => ack(a.id, "acknowledged")} className="text-[#2D5A27]">Acknowledge</button>
                  <button onClick={() => ack(a.id, "closed")} className="text-[#C25D38]">Close</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// ------------- Leaflet variant -------------
function FitAll({ points }) {
  const map = useMap();
  useEffect(() => {
    // Iter 172 · defensive — filter (0,0) & invalid coords client-side
    // in case the backend regresses. Cap max zoom so a small cluster
    // doesn't drop into street level.
    const clean = points.filter(p =>
      Number.isFinite(p.lat) && Number.isFinite(p.lng)
        && (Math.abs(p.lat) > 0.001 || Math.abs(p.lng) > 0.001));
    if (!clean.length) return;
    const b = L.latLngBounds(clean.map(p => [p.lat, p.lng]));
    map.fitBounds(b, { padding: [40, 40], maxZoom: 14 });
  }, [points, map]);
  return null;
}

function LeafletMap({ center, showStaff, showCustomers, locations, customers }) {
  const points = useMemo(() => {
    const arr = [];
    // Iter 173 · exclude stale staff (updated_at > 5 min) from the
    // auto-fit bounds so a handful of pings from last month don't drag
    // the viewport out to Bengaluru or wider. Still render them on the
    // map (grey) so ops can see they exist.
    if (showStaff) locations.forEach(l => {
      if (!l.stale) arr.push({ lat: l.latitude, lng: l.longitude });
    });
    if (showCustomers) customers.forEach(c => arr.push({ lat: c.latitude, lng: c.longitude }));
    return arr;
  }, [showStaff, showCustomers, locations, customers]);

  return (
    <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }}>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM'/>
      <FitAll points={points}/>
      {showStaff && locations.map(loc => (
        <CircleMarker key={`s${loc.staff_id}-${loc.moving ? "m" : loc.stale ? "x" : "s"}`}
                      center={[loc.latitude, loc.longitude]}
                      radius={loc.moving ? 12 : 8}
                      pathOptions={{
                        color: loc.moving ? "#2D5A27" : loc.stale ? "#8A8780" : "#1F3A57",
                        fillColor: loc.moving ? "#2D5A27" : loc.stale ? "#B8B4A9" : "#1F3A57",
                        fillOpacity: loc.moving ? 0.9 : 0.75,
                        weight: 2,
                        className: loc.moving ? "live-staff-pulse" : "",
                      }}>
          <Popup>
            <div className="font-medium">{loc.staff_name}</div>
            <div className="text-xs text-[#5C5A55]">
              {loc.role} · {loc.activity}
              {loc.moving && <span className="ml-1 text-[#2D5A27] font-semibold">· moving</span>}
              {loc.stale && <span className="ml-1 text-[#8A8780]">· stale</span>}
            </div>
            <div className="text-xs font-mono text-[#8A8780] mt-1">
              {loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}
            </div>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`}
               target="_blank" rel="noreferrer"
               className="text-[11px] text-[#1F3A57] underline mt-1 inline-flex items-center gap-1">
              <NavigationArrow size={10} weight="fill"/>Navigate
            </a>
          </Popup>
        </CircleMarker>
      ))}
      {showCustomers && customers.map(c => (
        <CircleMarker key={"c" + c.id}
                      center={[c.latitude, c.longitude]}
                      radius={5}
                      pathOptions={{
                        color: c.priority_tier === "VVIP" ? "#7A2E18"
                              : c.priority_tier === "VIP"  ? "#D4A017"
                              :                              "#3A7033",
                        fillColor: c.priority_tier === "VVIP" ? "#7A2E18"
                                  : c.priority_tier === "VIP"  ? "#D4A017"
                                  :                              "#3A7033",
                        fillOpacity: 0.7, weight: 1,
                      }}>
          <Popup>
            <div className="font-medium">{c.name}</div>
            <div className="text-[10px] text-[#5C5A55]">
              {c.customer_code} {c.priority_tier && `· ${c.priority_tier}`}
            </div>
            <div className="text-[10px] text-[#8A8780]">
              Field staff: {c.responsible_field_staff_name || "—"}
            </div>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${c.latitude},${c.longitude}`}
               target="_blank" rel="noreferrer"
               className="text-[11px] text-[#1F3A57] underline mt-1 inline-flex items-center gap-1">
              <NavigationArrow size={10} weight="fill"/>Navigate
            </a>
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}

// ------------- Google Maps variant -------------
function GoogleMap({ center, showStaff, showCustomers, locations, customers }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => { if (!cancelled) setReady(true); })
      .catch(err => toast.error(String(err)));
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!ready || !divRef.current || mapRef.current) return;
    mapRef.current = new window.google.maps.Map(divRef.current, {
      center: { lat: center[0], lng: center[1] }, zoom: 11,
      mapTypeControl: false, streetViewControl: false, gestureHandling: "greedy",
    });
  }, [ready, center]);
  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return;
    const G = window.google.maps;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    const bounds = new G.LatLngBounds();
    if (showStaff) {
      locations.forEach(loc => {
        const marker = new G.Marker({
          position: { lat: loc.latitude, lng: loc.longitude },
          map: mapRef.current,
          title: `${loc.staff_name} · ${loc.activity || ""}`,
          icon: {
            path: G.SymbolPath.CIRCLE,
            scale: loc.moving ? 11 : 7,
            fillColor: loc.moving ? "#2D5A27" : loc.stale ? "#B8B4A9" : "#1F3A57",
            fillOpacity: 0.9,
            strokeColor: "#FFFFFF", strokeWeight: 2,
          },
          zIndex: loc.moving ? 900 : 800,
        });
        const info = new G.InfoWindow({
          content: `<div style="font-family:sans-serif;font-size:12px;padding:4px 6px;">
            <div style="font-weight:600;">${loc.staff_name}</div>
            <div style="color:#5C5A55;">${loc.role || ""} · ${loc.activity || ""}${loc.moving ? " · moving" : loc.stale ? " · stale" : ""}</div>
            <div style="color:#8A8780;font-family:monospace;font-size:10px;">${loc.latitude.toFixed(5)}, ${loc.longitude.toFixed(5)}</div>
            <a href="https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}" target="_blank" style="color:#1F3A57;font-size:11px;">Navigate ↗</a>
          </div>`,
        });
        marker.addListener("click", () => info.open(mapRef.current, marker));
        markersRef.current.push(marker);
        bounds.extend(marker.getPosition());
      });
    }
    if (showCustomers) {
      customers.forEach(c => {
        const color = c.priority_tier === "VVIP" ? "#7A2E18"
                     : c.priority_tier === "VIP"  ? "#D4A017"
                     :                              "#3A7033";
        const marker = new G.Marker({
          position: { lat: c.latitude, lng: c.longitude },
          map: mapRef.current,
          icon: {
            path: G.SymbolPath.CIRCLE, scale: 5,
            fillColor: color, fillOpacity: 0.8,
            strokeColor: "#FFFFFF", strokeWeight: 1,
          },
          zIndex: 600,
        });
        const info = new G.InfoWindow({
          content: `<div style="font-family:sans-serif;font-size:12px;padding:4px 6px;">
            <div style="font-weight:600;">${c.name}</div>
            <div style="color:#5C5A55;font-size:10px;">${c.customer_code || ""} ${c.priority_tier ? "· " + c.priority_tier : ""}</div>
            <div style="color:#8A8780;font-size:10px;">Field staff: ${c.responsible_field_staff_name || "—"}</div>
            <a href="https://www.google.com/maps/dir/?api=1&destination=${c.latitude},${c.longitude}" target="_blank" style="color:#1F3A57;font-size:11px;">Navigate ↗</a>
          </div>`,
        });
        marker.addListener("click", () => info.open(mapRef.current, marker));
        markersRef.current.push(marker);
        bounds.extend(marker.getPosition());
      });
    }
    if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, 60);
  }, [ready, showStaff, showCustomers, locations, customers]);
  return <div ref={divRef} className="w-full h-full"/>;
}

const Kpi = ({ label, value, icon: Icon, accent, danger }) => (
  <div className="stat-card">
    <div className="flex items-start justify-between">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${danger ? "bg-[#C25D38]/10" : accent ? "bg-[#2D5A27]/12" : "bg-[#F7F6F2]"}`}>
        <Icon size={18} color={danger ? "#C25D38" : "#2D5A27"} weight="duotone"/>
      </div>
    </div>
    <div className="mt-6">
      <div className="font-heading text-3xl">{value ?? "—"}</div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mt-1">{label}</div>
    </div>
  </div>
);

// ============================================================
// Iter 241 · GPS history map + side panel
// Iter 242 · Replay mode — animate the day's trail at 60× speed
// ============================================================
function HistoryMap({ trail, loading }) {
  const [replayIdx, setReplayIdx] = useState(0);        // segment index
  const [playing, setPlaying]     = useState(false);
  const [speedX, setSpeedX]       = useState(60);       // playback multiplier
  const tickRef = useRef(null);

  // Reset when trail changes.
  useEffect(() => { setReplayIdx(0); setPlaying(false); }, [trail?.staff_id, trail?.first_ping]);

  // Playback loop — walks the segments[] array, advancing based on
  // each segment's real duration ÷ speed. 60× means every real-world
  // minute collapses to 1 second on screen.
  useEffect(() => {
    if (!playing || !trail?.segments?.length) return;
    if (replayIdx >= trail.segments.length - 1) { setPlaying(false); return; }
    const seg = trail.segments[replayIdx];
    const realMs = Math.max(100, Math.min(5000,
      ((seg.duration_s || 1) * 1000) / Math.max(speedX, 1)));
    tickRef.current = setTimeout(
      () => setReplayIdx(i => i + 1), realMs);
    return () => clearTimeout(tickRef.current);
  }, [playing, replayIdx, speedX, trail]);

  if (loading) return <div className="h-full flex items-center justify-center text-sm text-[#8A8780]">Loading history…</div>;
  if (!trail || !trail.segments?.length) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-[#8A8780] bg-[#F7F6F2]">
        No GPS trail for this staff on this date.
      </div>
    );
  }
  const path = [
    [trail.segments[0].a_lat, trail.segments[0].a_lng],
    ...trail.segments.map(s => [s.b_lat, s.b_lng]),
  ];
  const first = path[0];
  const last  = path[path.length - 1];
  const cur   = trail.segments[Math.min(replayIdx, trail.segments.length - 1)];
  const curPos = [cur.b_lat, cur.b_lng];
  const curTime = cur.end || cur.start;

  return (
    <div className="h-full relative">
      <MapContainer center={first} zoom={14} style={{ height: "100%", width: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM'/>
        <FitAll points={path.map(([lat, lng]) => ({ lat, lng }))}/>
        {/* Render past segments at full opacity, future at low opacity */}
        {trail.segments.map((s, i) => {
          const passed = i <= replayIdx;
          return (
            <Polyline key={i}
                        positions={[[s.a_lat, s.a_lng], [s.b_lat, s.b_lng]]}
                        pathOptions={{
                          color: s.moving ? "#2D5A27" : "#8A8780",
                          weight: s.moving ? 4 : 2,
                          opacity: passed ? (s.moving ? 0.9 : 0.5) : 0.15,
                          dashArray: s.moving ? null : "5, 6",
                        }}/>
          );
        })}
        {/* Start / End */}
        <CircleMarker center={first} radius={9}
                         pathOptions={{ color: "#166534", fillColor: "#22C55E", fillOpacity: 1 }}>
          <Popup>Start · {trail.first_ping?.slice(11, 16)}</Popup>
        </CircleMarker>
        <CircleMarker center={last} radius={9}
                         pathOptions={{ color: "#9A3412", fillColor: "#F97316", fillOpacity: 0.7 }}>
          <Popup>End · {trail.last_ping?.slice(11, 16)}</Popup>
        </CircleMarker>
        {trail.stops?.map((s, i) => (
          <CircleMarker key={`stop-${i}`} center={[s.lat, s.lng]} radius={11}
                           pathOptions={{ color: "#B45309", fillColor: "#FBBF24",
                                           fillOpacity: 0.9 }}>
            <Popup>
              <div className="text-xs">
                <div className="font-semibold">Stop · {Math.round(s.duration_s / 60)} min</div>
                <div className="text-[11px] text-[#5C5A55]">
                  {s.arrived_at?.slice(11, 16)} → {s.left_at?.slice(11, 16)}
                </div>
                <div className="text-[11px] text-[#8A8780]">{s.visits} pings in this cluster</div>
              </div>
            </Popup>
          </CircleMarker>
        ))}
        {/* The animated "current position" cursor — pulsing indigo dot */}
        <CircleMarker center={curPos} radius={10}
                         pathOptions={{
                           color: "#4338CA", fillColor: "#6366F1",
                           fillOpacity: 1, weight: 3,
                         }}>
          <Popup>
            <div className="text-xs">
              <div className="font-semibold">Now at: {curTime?.slice(11, 16)}</div>
              <div className="text-[11px]">
                {cur.moving ? "🟢 Moving" : "⚪ Idle"} · {Math.round(cur.distance_m)} m · {Math.round(cur.speed_kmph)} km/h
              </div>
            </div>
          </Popup>
        </CircleMarker>
      </MapContainer>
      {/* Replay overlay (iter 242) */}
      <div className="absolute left-3 right-3 bottom-3 z-[500] bg-white/95 backdrop-blur-sm
                        border border-[#E5E2D9] rounded-lg px-3 py-2 shadow-md
                        flex items-center gap-2 flex-wrap"
             data-testid="replay-overlay">
        <button onClick={() => {
                    if (replayIdx >= trail.segments.length - 1) setReplayIdx(0);
                    setPlaying(p => !p);
                  }}
                  data-testid="replay-toggle"
                  className={`h-8 w-8 rounded-full flex items-center justify-center ${
                    playing ? "bg-[#B45309] text-white" : "bg-[#4338CA] text-white"
                  }`}>
          {playing ? "▮▮" : "▶"}
        </button>
        <button onClick={() => { setReplayIdx(0); setPlaying(false); }}
                  data-testid="replay-reset"
                  className="h-8 px-2 rounded border border-[#E5E2D9] text-[11px] text-[#5C5A55] hover:bg-[#F7F6F2]">
          ↺
        </button>
        <div className="flex-1 flex items-center gap-2 min-w-[140px]">
          <input type="range" min="0" max={trail.segments.length - 1}
                   value={replayIdx}
                   onChange={e => { setReplayIdx(+e.target.value); setPlaying(false); }}
                   data-testid="replay-scrub"
                   className="w-full accent-[#4338CA]"/>
        </div>
        <div className="text-[10px] font-mono text-[#5C5A55] tabular-nums">
          {curTime?.slice(11, 16) || "--:--"}
        </div>
        <select value={speedX} onChange={e => setSpeedX(+e.target.value)}
                  data-testid="replay-speed"
                  className="h-8 px-2 rounded border border-[#E5E2D9] text-[11px] bg-white">
          {[30, 60, 120, 240, 600].map(s => (
            <option key={s} value={s}>{s}×</option>
          ))}
        </select>
      </div>
    </div>
  );
}


function HistoryPanel({ data, loading, selectedId, onSelect }) {
  if (loading) {
    return <div className="px-6 py-4 border-t border-[#E5E2D9] text-sm text-[#8A8780]">
      Loading GPS history…
    </div>;
  }
  if (!data || !data.bundles?.length) {
    return (
      <div className="px-6 py-6 border-t border-[#E5E2D9] bg-[#F7F6F2] text-sm text-[#8A8780] text-center"
             data-testid="history-empty">
        No GPS pings recorded for this date. GPS history retention is 30 days.
      </div>
    );
  }
  const sel = selectedId
    ? data.bundles.find(b => b.staff_id === selectedId)
    : data.bundles[0];

  const fmtDur = (s) => {
    if (!s) return "0";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    return `${h}h ${m - h * 60}m`;
  };
  const fmtDist = (m) => m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
  return (
    <div className="border-t border-[#E5E2D9] bg-[#FAFAF7]" data-testid="history-panel">
      <div className="px-6 py-3 border-b border-[#E5E2D9] flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-[#8A8780]">Staff on this day:</span>
        {data.bundles.map(b => (
          <button key={b.staff_id} onClick={() => onSelect(b.staff_id)}
                    data-testid={`history-staff-${b.staff_id}`}
                    className={`h-8 px-3 rounded-full text-[11px] font-medium border ${
                      (sel?.staff_id === b.staff_id)
                        ? "bg-[#4338CA] text-white border-[#4338CA]"
                        : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F3F4F6]"}`}>
            {b.staff_name} · {b.point_count} pts
          </button>
        ))}
      </div>
      {sel && (
        <div className="p-6 space-y-4" data-testid={`history-detail-${sel.staff_id}`}>
          <div>
            <div className="font-heading text-base">{sel.staff_name}</div>
            <div className="text-[11px] text-[#8A8780]">{sel.role || "—"} · {sel.phone || ""}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiPill label="Total distance" value={fmtDist(sel.total_distance_m)} accent="#2D5A27"/>
            <KpiPill label="Moving"         value={fmtDur(sel.moving_time_s)}      accent="#166534"/>
            <KpiPill label="Idle"           value={fmtDur(sel.idle_time_s)}        accent="#8A8780"/>
            <KpiPill label="Stops"          value={String(sel.stops?.length || 0)} accent="#B45309"/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">
              Stops · {sel.stops?.length || 0}
            </div>
            {!sel.stops?.length ? (
              <div className="text-xs text-[#8A8780] italic">No long idle clusters detected.</div>
            ) : (
              <div className="space-y-1">
                {sel.stops.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-[#F1EFE7] last:border-0">
                    <span className="font-mono text-[#5C5A55]">
                      {s.arrived_at.slice(11, 16)} → {s.left_at.slice(11, 16)}
                    </span>
                    <span className="text-[#1A1A1A]">{fmtDur(s.duration_s)} · {s.visits} pings</span>
                    <a href={`https://www.google.com/maps?q=${s.lat},${s.lng}`}
                         target="_blank" rel="noreferrer"
                         className="text-[#4338CA] underline text-[11px]">open in maps</a>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">
              Movement timeline
            </div>
            <div className="h-6 flex rounded overflow-hidden border border-[#E5E2D9]">
              {sel.segments?.map((s, i) => (
                <div key={i} title={`${s.moving ? "Moving" : "Idle"} · ${fmtDist(s.distance_m)} · ${fmtDur(s.duration_s)}`}
                       style={{
                         flex: Math.max(s.duration_s, 1),
                         background: s.moving ? "#166534" : "#D4D1C7",
                       }}/>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-[#8A8780] mt-1 font-mono">
              <span>{sel.first_ping?.slice(11, 16)}</span>
              <span>{sel.last_ping?.slice(11, 16)}</span>
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-[#8A8780]">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded bg-[#166534]"/>moving</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded bg-[#D4D1C7]"/>idle</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiPill({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
      <div className="font-heading text-lg mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

